import { useMutation, useQuery } from '@apollo/client';
import Build from '@clearkit/icons/glyphs/Build';
import { CKBox, CKButton, CKCardCollapsible, CKModal } from 'clearkit';
import _ from 'lodash';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import { useMemo } from 'react';

import SearchBuilder, { validateSearch } from '~/components/SearchBuilder';
import {
  EMPTY_STATE_SEARCH,
  HUBSPOT_SUB_CATEGORIES,
  SALESFORCE_SUB_CATEGORIES,
} from '~/components/SearchBuilder/utils/constants';
import ToastsContainer from '~/containers/Toasts';
import {
  getAttributeType,
  getEmptyStateSearch,
  getEmptyStateSubObject,
} from '~/lib/selectiveEnrichment';

import EmptyState from '../FieldMappingForm/EmptyState.svg';
import disable from './disable.graphql';
import EmptyStateHubspot from './EmptyStateSVGs/EmptyStateHubspot.svg';
import EmptyStateSalesforce from './EmptyStateSVGs/EmptyStateSalesforce.svg';
import mutation from './mutation.graphql';
import query from './query.graphql';
import { ATTRIBUTE_TYPES_TO_REMOVE } from './utils';

const SelectiveEnrichmentConfig = ({ objectType, integration }) => {
  const attributeType = getAttributeType(integration, objectType);
  const salesforceSubCategories = SALESFORCE_SUB_CATEGORIES[attributeType];
  const hubspotSubCategories = HUBSPOT_SUB_CATEGORIES[attributeType];

  // Pull account connector w/ any previously saved selective enrichment filters +
  // attributes + salesforce users to populate trait selector dropdown
  const {
    data: { searchAttributes: attributes, salesforceUsers, accountConnector },
  } = useQuery(query, {
    returnPartialData: true,
    variables: {
      type: attributeType,
      externalObjectType: objectType.name,
      service: integration,
      integrationCategory: integration,
      collectionType: attributeType,
      salesforceSubCategories: salesforceSubCategories,
      hubspotSubCategories: hubspotSubCategories,
    },
  });
  const savedSelectiveEnrichmentConfig =
    accountConnector?.selectiveEnrichmentFilters?.filters;
  const [filters, setFilters] = useState(savedSelectiveEnrichmentConfig);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const filtersAreValid = validateSearch(filters).length === 0;
  const input = useMemo(
    () => ({
      id: accountConnector?.id,
      externalObjectType: capitalize(objectType.name),
      filters,
    }),
    [accountConnector, filters, objectType],
  );

  const filteredAttributes = useMemo(
    () =>
      attributes?.filter(
        (attr) => !ATTRIBUTE_TYPES_TO_REMOVE.includes(attr?.details?.type),
      ),
    [objectType.name, attributes, filters],
  );

  const updateFilters = (fields) => {
    if (_.isEqual(fields.search, EMPTY_STATE_SEARCH)) {
      setConfirmationModalOpen(true);
    } else {
      setFilters(fields.search);
    }
  };

  useEffect(() => {
    if (savedSelectiveEnrichmentConfig) {
      setFilters(savedSelectiveEnrichmentConfig);
    }
  }, [objectType.name, savedSelectiveEnrichmentConfig]);

  const [
    updateSelectiveEnrichmentConfig,
    { loading: isUpdating },
  ] = useMutation(mutation, {
    onCompleted: () => {
      ToastsContainer.addSuccess('Selective sync saved');
    },
    onError: () => {
      ToastsContainer.addWarning(
        'An error occurred when saving your selective sync criteria, please try again.',
      );
    },
    refetchQueries: ['SelectiveEnrichmentConfig'],
    variables: {
      input,
    },
  });

  const [
    deleteSelectiveEnrichmentConfig,
    { loading: isDeleting },
  ] = useMutation(disable, {
    onCompleted: () => {
      ToastsContainer.addSuccess('Selective sync removed');
      setFilters(null);
    },
    onError: () => {
      ToastsContainer.addWarning(
        'An error occurred when removing your selective sync criteria, please try again.',
      );
    },
    variables: {
      input,
    },
  });

  const disableSelectiveEnrichment = () => {
    delete input.filters;
    deleteSelectiveEnrichmentConfig().then(() =>
      setConfirmationModalOpen(false),
    );
  };

  const areButtonsDisabled = useMemo(() => {
    if (savedSelectiveEnrichmentConfig) {
      return isEqual(filters, savedSelectiveEnrichmentConfig);
    }
    return isEqual(filters, undefined);
  }, [filters, savedSelectiveEnrichmentConfig]);

  return (
    <div className="px-4">
      <CKCardCollapsible>
        <CKCardCollapsible.Header>
          <SelectiveEnrichmentHeader
            integration={integration}
            objectType={objectType}
          />
        </CKCardCollapsible.Header>
        <CKCardCollapsible.Body>
          <SelectiveEnrichmentBody
            integration={integration}
            objectType={objectType}
            setFilters={setFilters}
            attributeType={attributeType}
            attributes={filteredAttributes}
            filters={filters}
            showInvalidFields={showInvalidFields}
            salesforceUsers={salesforceUsers}
            updateFilters={updateFilters}
          />
          <CKCardCollapsible.Footer>
            <div className="flex items-center justify-end space-x-3">
              <CKButton
                onClick={() => setFilters(savedSelectiveEnrichmentConfig)}
                isDisabled={areButtonsDisabled}
                variant="simple"
                variantColor="default">
                Cancel
              </CKButton>
              <SaveSelectiveEnrichmentConfigButton
                isLoading={isUpdating}
                filtersAreValid={filtersAreValid}
                setShowInvalidFields={setShowInvalidFields}
                updateSelectiveEnrichmentConfig={
                  updateSelectiveEnrichmentConfig
                }
                areButtonsDisabled={areButtonsDisabled}
                filters={filters}
              />
            </div>
          </CKCardCollapsible.Footer>
        </CKCardCollapsible.Body>
      </CKCardCollapsible>
      <DisableSelectiveEnrichmentModal
        objectType={objectType}
        deleteSelectiveEnrichmentConfig={disableSelectiveEnrichment}
        isLoading={isDeleting}
        confirmationModalOpen={confirmationModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        integration={integration}
      />
    </div>
  );
};

function SaveSelectiveEnrichmentConfigButton({
  updateSelectiveEnrichmentConfig,
  filtersAreValid,
  setShowInvalidFields,
  isLoading,
  areButtonsDisabled,
}) {
  return (
    <div
      onMouseEnter={() => setShowInvalidFields(true)}
      onMouseLeave={() => setShowInvalidFields(false)}>
      <CKButton
        isDisabled={!filtersAreValid || isLoading || areButtonsDisabled}
        variant="bold"
        dataTestId="saveSelectiveEnrichmentConfig"
        onClick={updateSelectiveEnrichmentConfig}
        variantColor="green">
        Save
      </CKButton>
    </div>
  );
}

function SelectiveEnrichmentHeader({ integration, objectType }) {
  return (
    <>
      <div
        className="grid grid-cols-[1fr,auto]"
        data-testid="selectiveEnrichmentConfigCardHeader">
        <div className="text-base">
          <h3 className="mb-2 font-medium">Selective Sync </h3>
          <p>
            Use selective sync to create an enrichment filter that will limit
            the sync of enriched data to your CRM to{' '}
            <span className="font-medium">
              {capitalize(integration)} {objectType.name}s that match criteria
              defined by you.
            </span>
          </p>
        </div>
      </div>
    </>
  );
}

function DisableSelectiveEnrichmentModal({
  deleteSelectiveEnrichmentConfig,
  isLoading,
  objectType,
  integration,
  confirmationModalOpen,
  setConfirmationModalOpen,
}) {
  return (
    <CKModal
      isVisible={confirmationModalOpen}
      onToggle={() => setConfirmationModalOpen(false)}
      size="xs"
      aria-label="Modal">
      <CKModal.Header className="pb-2">
        <CKModal.Heading>
          Are you sure you want to remove the selective sync conditions for{' '}
          {objectType.name}s?
        </CKModal.Heading>
      </CKModal.Header>
      <CKModal.Body className="px-10 pt-2 pb-10">
        <p>
          If no selective sync conditions are present, selective sync will be
          disabled, and Clearbit will enrich and sync data for{' '}
          <strong className="font-semibold">all</strong>{' '}
          {capitalize(integration)} {objectType.name}s.{' '}
          {capitalize(integration)} {objectType.name}s previously enriched by
          Clearbit will retain that data.
        </p>
      </CKModal.Body>
      <CKModal.Footer className="gap-2">
        <CKButton onClick={() => setConfirmationModalOpen(false)}>
          Dismiss
        </CKButton>
        <CKButton
          isLoading={isLoading}
          variant="bold"
          onClick={() => deleteSelectiveEnrichmentConfig(false)}
          variantColor="red">
          Yes, remove and disable selective sync
        </CKButton>
      </CKModal.Footer>
    </CKModal>
  );
}

function SelectiveEnrichmentBody({
  integration,
  objectType,
  setFilters,
  attributeType,
  attributes,
  salesforceUsers,
  filters,
  updateFilters,
  showInvalidFields,
}) {
  return !filters ? (
    <EmptyStateSelectiveEnrichment
      integration={integration}
      objectType={objectType}
      setFilters={setFilters}
    />
  ) : (
    <SearchBuilder
      type={attributeType}
      search={filters}
      attributes={attributes}
      isSelectiveEnrichmentConfig
      salesforceUsers={salesforceUsers}
      showInvalidFields={showInvalidFields}
      onChange={(search) => {
        updateFilters({ search });
      }}
      emptyGroup={getEmptyStateSubObject(integration, objectType)}
    />
  );
}

function EmptyStateIcon({ integration }) {
  switch (integration) {
    case 'salesforce':
      return <EmptyStateSalesforce />;
    case 'hubspot':
      return <EmptyStateHubspot />;
    default:
      return <EmptyState />;
  }
}

function EmptyStateSelectiveEnrichment({
  integration,
  objectType,
  setFilters,
}) {
  return (
    <CKBox
      className="flex flex-col items-center p-10 m-10"
      variant={CKBox.Variant['tinted-frame']}>
      <EmptyStateIcon integration={integration} />
      <p className="text-base font-medium text-center text-gray-800 mt-7">
        Get selective around sync
      </p>
      <p className="max-w-md mt-2 text-sm text-center text-gray-600">
        Before you can use selective sync for Salesforce {objectType.name}s,
        you’ll first need to define the criteria around which {objectType.name}s
        we should sync.
      </p>
      <div className="mt-6">
        <CKButton
          variant="bold"
          variantColor="blue"
          onClick={() => {
            setFilters(getEmptyStateSearch(integration, objectType));
          }}
          className="mr-4"
          leftIcon={<Build />}>
          Set up selective sync for Salesforce {objectType.name}s
        </CKButton>
      </div>
    </CKBox>
  );
}

export default SelectiveEnrichmentConfig;
